import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "umi";
import moment from "moment";
import { isEmpty } from "lodash-es";
import RangePicker, { IRangePickerRef } from '@home/components/RangePicker';
import { getFilterDateValue } from "@/pages/datalab/utils/utils";
import { Operation } from '@dbl/core/filter';
import { getDateInfo } from "@/pages/datalab/pages/WorkBench/CreateReport/utils";
import classNames from "classnames";
import { getLocaleState } from "@home/utils/state";
import { DATALAB_DATE_EVENT } from "@dbl/core/global";
import type { TDatalabDateEvent } from '@dbl/core/global';
import DateEventShortcut from "@/pages/datalab/components/DateComponents/DateEventShortcut";
import BaseTimeAndCompareRange from "@/pages/datalab/components/DateComponents/BaseTimeAndCompareRange";
import CompareSwitch from "@/pages/datalab/components/DateComponents/CompareSwitch";
import { getDatalabShortcut } from '@/pages/datalab/components/DateComponents/utils';
import { getPreviousPeriodValue } from '@dbl/core/date';
// import {adjustOverlayPosition} from "@/pages/datalab/utils/overlayPositionAdjuster";

interface ITimeValue {
  value?: moment.Moment[];
  type?: string;
  dateType?: string;
  dateRange?: string[];
  defaultCompareTimes?: any[];
  isDisabledCompare?: boolean;
}

const START_OF_DATE = '2020-01-01';
const START_OF_WEEK = 1;

const DateFilter = (props: any, ref: any) => {
  const { filterInfo, innerFilterValueMapRef, setOpenStatus, setVisibleScope, eventEmitter } = props;
  const { i: key, filterName, relevantFilters } = filterInfo || {};
  const filterValue = innerFilterValueMapRef.current?.[key] ?? {};
  const { locale } = useIntl();
  const isConfirmRef = useRef<boolean>(false);
  const pickerRef = useRef<IRangePickerRef | null>(null);

  const { data: seasonInfo } = (getLocaleState(DATALAB_DATE_EVENT) || {}) as TDatalabDateEvent;

  const [timeValue, setTimeValue] = useState<ITimeValue>({}); // setTimeValue 用来触发内部变更
  const [quickValue, setQuickValue] = useState({
    tab: 'common',
  });

  useEffect(() => {
    const { dateRange, dateType } = getDateInfo(filterValue?.value ?? [], filterValue?.dateType, filterValue?.last_day, filterValue?.lastCount);
    const value = dateRange.map((date) => moment(date));
    const type = dateType === 'day' ? 'date' : dateType;
    setTimeValue({ value, type, dateType, dateRange, defaultCompareTimes: filterValue?.compare_time || [] });
    setQuickValue(filterValue?.quick_value || {});
  }, [filterValue]);

  const handleChangeDate = ({ dateRange, dateType, compareTimes, quickValue }) => {
    if (innerFilterValueMapRef?.current) {
      /**
       * 页面初始化时, 筛选器innerFilterValueMapRef.current[key] 中已经存有 table_id, column_type等筛选器信息了.
       * 报表顶部日期变动 只需要改变 value, dateType 和 operation 的值, 其他值无需变动.
       */
      innerFilterValueMapRef.current[key] = Object.assign(innerFilterValueMapRef.current[key], {
        value: dateRange,
        // filterName,
        // timeType: type,
        dateType,
        // defaultValue,
        // defaultType,
        // column,
        operation: Operation.Between,
        // table_id: dataset,
        // column_type: filterType,
        compare_time: compareTimes,
        quick_value: quickValue,
      });
    }
    // 当日期变更时
    if (relevantFilters?.length > 0) {
      relevantFilters.map(child => {
        // 父筛选器 触发事件, 子筛选器的唯一标识符i作为事件名称
        eventEmitter.emit(child?.i, filterInfo);
      });
    }
  };
  const commonDataList = useMemo(()=> {
    const commonDataListObj: any = getDatalabShortcut(START_OF_DATE, {});
    const commonDataList = commonDataListObj?.[timeValue?.type];
    return commonDataList;
  },[timeValue?.type]);
  
  const handleClose = () => {
    pickerRef.current?.close();
  };

  return <div ref={ref} className={`search-item-date search-item-date-${key} ${filterName ? 'search-item-date-name' : ''}`}>
    <RangePicker
      ref={pickerRef}
      className="search-border"
      overlayClassName={classNames('dbd-range-picker-overlay dbl-filter-date-overlay', { 'dbl-filter-date-hour': timeValue.type === 'hour' })}
      type={timeValue.type}
      key={key}
      latestDate={{}}
      value={timeValue.value}
      startOfTime={START_OF_DATE}
      typeList={['hour', 'date', 'week', 'month']}
      showSelectType={true}
      startOfWeek={START_OF_WEEK}
      language={locale === 'zh-CN' ? 'cn' : 'en'}
      // eslint-disable-next-line arrow-body-style
      setShortcutOption={() => {
        return <DateEventShortcut
          startOfTime={START_OF_DATE}
          latestDate={{}}
          commonDataList={commonDataList}
          type={timeValue?.type}
          seasonInfo={seasonInfo}
          quickValue={quickValue}
          changeQuickValue={setQuickValue}
          // changeClose={()=> handleClose?.()}
          // 底层调用到onChange方法，在onChange中处理
          // handleChangeValue={(value: any, type: string, tabActiveTab: string) => {
          // }}
        />;
      }}
      footerConfig={{
        left: (<CompareSwitch
          value={timeValue?.defaultCompareTimes?.length > 0}
          onChange={(open: boolean) => {
            if (open) {
              const compare = getPreviousPeriodValue(timeValue?.dateRange, timeValue?.dateType);
              setTimeValue((timeValue) => ({...timeValue, defaultCompareTimes: [compare]}));
            }
          }}
          onConfirm={() => {
            isConfirmRef.current = true;
            handleClose?.();
            handleChangeDate({dateRange: timeValue.dateRange, dateType: timeValue.dateType, compareTimes: [], quickValue});
          }}
          onCancel={handleClose}
          disabledSwitch={timeValue.isDisabledCompare}
        />)
      }}
      changeClose={false}
      onChange={(value: any, otype: string, others: any) => {
        const { tab, quickValue } = others || {};
        // 选择事件切换到日粒度面板
        const type = tab === 'event' ? 'date' : otype;
        const dateRange = getFilterDateValue(value, type);
        const dateType = type === 'date' ? 'day' : type;
        if (type !== timeValue.type) { 
          // 说明是在侧边直接切换的粒度, 没有浮窗的确认按钮, 切换粒度后直接生效
          // 切换粒度时，快捷选项清空
          const newQuickValue = quickValue === undefined ? { tab: quickValue?.tab || 'common' } : quickValue;
          setQuickValue(newQuickValue);
          handleChangeDate({ dateRange, dateType, compareTimes: [], quickValue: newQuickValue });
        }
        setTimeValue({
          value,
          type,
          dateType,
          dateRange,
          defaultCompareTimes: type !== timeValue.type ? [] : timeValue.defaultCompareTimes,
          isDisabledCompare: timeValue.isDisabledCompare,
        });
      }}
      isReseted={false}
      defaultValue={{
        hour: [moment().startOf('day'), moment().endOf('day')],
        date: [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
        week: [moment().weekday(-7 * 1), moment().weekday(-1)],
        month: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
      }}
      onOpenChange={(open: any) => {
        if (open) {
          // 重置confirm状态
          isConfirmRef.current = false;
          // 打开弹窗时, 判断是否可以设置对比时间
          const isDisabledCompare = Object.values(innerFilterValueMapRef.current).some((item: any) => item.filterType === 'date' && item.filterKey !== filterValue.filterKey && !isEmpty(item.compare_time));
          setTimeValue({
            ...timeValue,
            isDisabledCompare,
          });
        }

        if (!open && (!isConfirmRef.current)) {
          // 关闭弹窗, 且未点击确认按钮, 将日期恢复成原来的日期
          const { dateRange, dateType } = getDateInfo(filterValue?.value ?? [], filterValue?.dateType, filterValue?.last_day, filterValue?.lastCount);
          const value = dateRange.map((date) => moment(date));
          const type = dateType === 'day' ? 'date' : dateType;
          setQuickValue(filterValue?.quick_value || {});
          setTimeValue({ value, type, dateType, dateRange, defaultCompareTimes: filterValue?.compare_time || [], isDisabledCompare: timeValue.isDisabledCompare });
        }
        setOpenStatus(open);
        open && setVisibleScope(false);
      }}
      rangePrefix={(<div className={'dbl-date-filter-name'} >{filterInfo?.filterName}</div>)}
      overlay={
        (!timeValue.isDisabledCompare && timeValue?.defaultCompareTimes?.length > 0) ?
          (<BaseTimeAndCompareRange
            value={{
              compareTimes: timeValue?.defaultCompareTimes,
              dateRange: timeValue?.dateRange,
              dateType: timeValue?.dateType,
              quickValue,
            }}
            onConfirm={(newValue) => {
              isConfirmRef.current = true;
              pickerRef.current?.close();
              handleChangeDate(newValue);
              // 改变筛选器内部对比时间状态
              const { dateType, dateRange, compareTimes } = newValue || {};
              
              // 使用setTimeout延迟更新状态, 可以避免在弹窗关闭的时候闪烁成另一个面板的情况.
              setTimeout(() => {
                setQuickValue(newValue?.quickValue || {});
                setTimeValue({
                  value: dateRange?.map(date => moment(date)),
                  type: dateType === 'day' ? 'date' : dateType,
                  dateType,
                  dateRange,
                  defaultCompareTimes: compareTimes || [],
                  isDisabledCompare: timeValue.isDisabledCompare
                });
              }, 300);
            }}
            onCancel={handleClose}
            mountClassNames={'.dbd-range-picker-overlay.dbl-filter-date-overlay'}
          />) : undefined
      }
    />
  </div>;
};

export default React.forwardRef(DateFilter);
